import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  FormContext,
  Modal,
  ModalContext,
  Toggle,
  toast,
  useServerResponseTranslation,
} from 'dash-ui';

import { ExternalSalesModalKeys } from 'pages/external-sales/constants/constants';
import { OrganizationUsersModalsEnum } from 'pages/organizations/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/constants/constants';
import {
  GetSaleTeamUsersDocument,
  GetSaleTeamsDocument,
  SaleTeamUser,
  useAddSaleTeamUserMutation,
  useEditSaleTeamUserMutation,
  useGetPropertiesQuery,
  useGetSalesTeamRolesQuery,
} from 'gql/generated.graphql';

import { FormFields } from './SaveSaleTeamUserModal.constants';
import useValidation from './hooks/useValidation';

const SaveSaleTeamUserModal = () => {
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const { isOpenCurrent, toggleModal, payload, setPayload } =
    useContext(ModalContext);
  const isOpen =
    isOpenCurrent[ExternalSalesModalKeys.SaveSaleTeamUser] ||
    isOpenCurrent[ExternalSalesModalKeys.EditSaleTeamUser] ||
    false;
  const isEdit =
    !!isOpenCurrent[ExternalSalesModalKeys.EditSaleTeamUser] || false;
  const {
    formState,
    stateHandlers,
    setFormValues,
    formErrors,
    resetFormErrors,
    resetForm,
  } = useContext(FormContext);
  const valid = useValidation();

  const translateServerErrors = useServerResponseTranslation();
  const formRef = useRef<HTMLFormElement | null>(null);
  const { data, loading: propertiesLoading } = useGetPropertiesQuery({
    variables: {
      pagination: { allowLimit: false },
      sort: [{ fieldName: 'name', sortOrder: 1 }],
    },
  });

  const { data: rolesData, loading: rolesLoading } =
    useGetSalesTeamRolesQuery();

  const [addSaleTeamUser, { loading: addLoading }] = useAddSaleTeamUserMutation(
    {
      refetchQueries: [GetSaleTeamUsersDocument, GetSaleTeamsDocument],
    }
  );
  const [editSaleTeamUser, { loading: editLoading }] =
    useEditSaleTeamUserMutation({
      refetchQueries: [GetSaleTeamUsersDocument],
    });

  useEffect(() => {
    if (payload && isEdit) {
      const user = payload as SaleTeamUser;

      setFormValues({
        firstName: user.profile.firstName,
        lastName: user.profile.lastName,
        role: user.role._id,
        email: user.account.email,
        excludeFromReports: user.account.excludeFromReports,
        properties: user.assignedPropertiesIds,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, isEdit]);

  const propertiesOptions = useMemo(() => {
    const properties = data?.getProperties.collection || [];

    return properties.map((property) => ({
      value: property._id,
      label: property.name,
    }));
  }, [data]);

  const rolesOptions = useMemo(() => {
    const roles = rolesData?.getSalesTeamRoles.collection || [];

    return roles.map((role) => ({
      value: role._id,
      label: t(`common:roles.${role.type}`),
    }));
  }, [rolesData, t]);

  const onRequestClose = () => {
    resetFormErrors();
    resetForm();
    setPayload(null);
    toggleModal(
      false,
      isEdit
        ? ExternalSalesModalKeys.EditSaleTeamUser
        : ExternalSalesModalKeys.SaveSaleTeamUser
    );
  };

  const handleTextValues =
    (field: FormFields) => (event: ChangeEvent<HTMLInputElement>) => {
      const handler = stateHandlers[field];
      handler(event.target.value);
      // valid(field, event.target.value);
    };

  const handleSelectValues =
    (field: FormFields) => (value: string, values: string[] | undefined) => {
      const handler = stateHandlers[field];
      handler(values || value);
      valid(field, value);
    };

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const {
      firstName,
      lastName,
      email,
      excludeFromReports,
      role,
      properties,
      ...profile
    } = formState;

    if (
      !valid(FormFields.FirstName, firstName) ||
      !valid(FormFields.LastName, lastName) ||
      !valid(FormFields.Email, email) ||
      !valid(FormFields.Role, role)
    )
      return;

    const mutation = isEdit ? editSaleTeamUser : addSaleTeamUser;

    mutation({
      variables: {
        input: {
          profileInput: {
            firstName: profile.firstName || firstName,
            lastName: profile.lastName || lastName,
          },
          accountInput: { email, excludeFromReports },
          saleTeamRoleId: role,
          saleTeamUserId: payload._id,
          assignedPropertiesIds: properties,
          saleTeamId: isEdit ? undefined : payload.saleTeamId,
        },
      },
      onCompleted: (userData: any) => {
        const dataKey = isEdit ? 'editSaleTeamUser' : 'addSaleTeamUser';
        const toastKey = isEdit ? 'userUpdated' : 'userAdded';
        onRequestClose();
        toast.success(t(`pages:externalSalesPage.toasts.${toastKey}`));
        toggleModal(
          true,
          OrganizationUsersModalsEnum.SEND_REGISTRATION_LINK_TO_USER
        );
        setPayload(userData[dataKey].accountId);
      },
      onError: (error) => {
        toast.error(translateServerErrors(error));
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={
        (isEdit
          ? t('pages:externalSalesPage.editSaleTeamUserModalLabel')
          : t('pages:externalSalesPage.addSaleTeamUserModalLabel')) || ''
      }
      submitButton={{
        isLoading: addLoading || editLoading,
        title: isEdit ? t('common:labels.edit') : t('common:labels.add'),
        onClick: () => {
          if (formRef) {
            formRef.current?.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: onRequestClose,
      }}
    >
      <Form ref={formRef} onSubmit={onFormSubmit}>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t('forms:labels.email')}</Form.Label>
          <Form.Input
            placeholder={t('forms:placeholders.email') || ''}
            value={formState.email || ''}
            onChange={handleTextValues(FormFields.Email)}
            $errorType={formErrors[FormFields.Email]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Email]?.type}>
            {formErrors[FormFields.Email]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t('forms:labels.firstName')}</Form.Label>
          <Form.Input
            placeholder={t('forms:placeholders.firstName') || ''}
            value={formState.firstName || ''}
            onChange={handleTextValues(FormFields.FirstName)}
            $errorType={formErrors[FormFields.FirstName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.FirstName]?.type}>
            {formErrors[FormFields.FirstName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t('forms:labels.lastName')}</Form.Label>
          <Form.Input
            placeholder={t('forms:placeholders.lastName') || ''}
            onChange={handleTextValues(FormFields.LastName)}
            value={formState.lastName || ''}
            $errorType={formErrors[FormFields.LastName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.LastName]?.type}>
            {formErrors[FormFields.LastName]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label required>{t('forms:labels.role')}</Form.Label>
          <Form.Select
            w="100%"
            isLoading={rolesLoading}
            options={rolesOptions}
            value={formState.role || ''}
            placeholder={t('forms:placeholders.role')}
            onChanged={handleSelectValues(FormFields.Role)}
            $errorType={formErrors[FormFields.Role]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Role]?.type}>
            {formErrors[FormFields.Role]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.properties')}</Form.Label>
          <Form.Select
            isMulti
            options={propertiesOptions}
            isLoading={propertiesLoading}
            value={formState.properties || []}
            isSelectAll
            onChanged={handleSelectValues(FormFields.Properties)}
            w="100%"
            placeholder={t('forms:placeholders.properties')}
          />
        </Form.Group>
        <Form.Group w="100%" mt={3}>
          <Form.Label>{t('forms:labels.excludeFromReports')}</Form.Label>
          <Toggle
            // @ts-ignore
            checked={formState.excludeFromReports}
            flexProps={{ mx: 2, my: 2 }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { checked } = e.target;
              stateHandlers[FormFields.ExcludeFromReports](checked);
            }}
          />
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default SaveSaleTeamUserModal;
